import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminScrubStructuralNonDivButton — Round P112d (Jul 2026).
 *
 * One-click admin trigger for the idempotent structural-non-dividend +
 * frequency-default scrub that fixes:
 *   • Warrants / units / rights (W / WS / U / UN / R / RT suffixes) that
 *     have leaked into `stock_cache` with Yahoo-scraped `dividendRate`
 *     from the parent common (BRUNW-style bug).
 *   • Quarterly-defaulted thin-history rows where `dividend_rate` is
 *     within 5% of `last_dividend_value` — flipped to Annually so the
 *     Dividend Info tab stops 4×-inflating income projections
 *     (BRIA-style bug).
 *
 * The button stays visible after use (endpoint is idempotent) and
 * shows a "Last run: <date> · <N structural + M frequency>" hint next
 * to it so the admin knows at a glance when it was last exercised.
 */
export default function AdminScrubStructuralNonDivButton() {
  const { user } = useAuth();
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/reconcile/scrub-structural-non-dividend/status`,
        { withCredentials: true },
      );
      setStatus(data);
    } catch {
      // silent — button still works even if status endpoint fails
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') loadStatus();
  }, [user, loadStatus]);

  if (user?.role !== 'admin') return null;

  const runScrub = async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/reconcile/scrub-structural-non-dividend`,
        {},
        { withCredentials: true },
      );
      if (data?.already_running) {
        toast.info('Scrub already running', { description: 'Waiting for the current run to finish…', duration: 4000 });
      } else {
        toast.info('Scrub started in the background', {
          description: 'Runs past the 2-minute request window when it has to pull history live. This card updates when it finishes.',
          duration: 5000,
        });
      }
      // Poll the status doc until the background run clears in_progress.
      const before = status?.last_run_at || null;
      for (let i = 0; i < 180; i += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await axios.get(
          `${API}/admin/reconcile/scrub-structural-non-dividend/status`,
          { withCredentials: true },
        );
        if (!st?.in_progress && st?.last_run_at && st.last_run_at !== before) {
          setStatus(st);
          const s = st.last_structural_count ?? 0;
          const f = st.last_frequency_count ?? 0;
          const total = s + f;
          toast.success(total === 0 ? 'Scrub complete — nothing to fix' : `Scrub complete — ${total} rows fixed`, {
            description: total === 0
              ? 'Every stock_cache row is already clean. Safe to close.'
              : `${s} structural / special-only + ${f} frequency-defaulted.`,
            duration: 6000,
          });
          break;
        }
      }
      await loadStatus();
    } catch (err) {
      toast.error('Scrub failed', {
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        duration: 6000,
      });
    } finally {
      setRunning(false);
    }
  };

  const lastRun = status?.last_run_at
    ? new Date(status.last_run_at).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    : null;
  const lastStructural = status?.last_structural_count;
  const lastFrequency = status?.last_frequency_count;

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={runScrub}
        disabled={running}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-900 dark:text-indigo-200 border border-indigo-500/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: idempotent scan that fixes warrant/unit/right rows with fake dividend data AND thin-history rows with wrong Quarterly frequency. Safe to run any time."
        data-testid="admin-scrub-structural-non-div-fire"
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        Scrub Bad Dividend Data
      </button>
      {lastRun && (
        <span
          className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300"
          data-testid="admin-scrub-last-run"
          title={`Last run by ${status?.last_run_by || 'admin'}`}
        >
          <CheckCircle2 className="w-3 h-3" />
          Last run: {lastRun}{status?.last_run_by === 'cron' ? ' (nightly)' : ''}
          {(lastStructural != null || lastFrequency != null) && (
            <span className="text-muted-foreground">
              · fixed {lastStructural ?? 0} struct + {lastFrequency ?? 0} freq
            </span>
          )}
          <span className="text-muted-foreground" data-testid="admin-scrub-nightly-note">· runs nightly 10:30 PM ET</span>
        </span>
      )}
    </div>
  );
}
